import { Bluesky, Pinterest, Threads, X } from '@sparemin/blockhead';
import React from 'react';

import { omit } from 'underscore';
import {
  DESTINATION_PRESETS,
  DestinationPlatform,
  DestinationPreset,
  FULLCLIP_PRESET,
  PlatformVideoType,
} from 'blocks/DestinationPlatforms';
import FacebookRounded from 'components/icons/FacebookRounded';
import InstagramRounded from 'components/icons/InstagramRounded';
import TiktokRounded from 'components/icons/TiktokRounded';
import YoutubeRounded from 'components/icons/YoutubeRounded';
import bem from 'utils/bem';
import { SocialPresetOption, SocialPresetPopoverConfig } from './types';

export const block = bem('audio-clipper');

export const SOCIAL_PRESET_BLOCKING_OPTIONS: DestinationPreset['key'][] = [
  'YouTubeShort',
  'InstagramReel',
  'InstagramStory',
  'InstagramPost',
  'FacebookReel',
  'FacebookStory',
  'FacebookPost',
  'TikTokPost',
  'TikTokStory',
];

function getPresetWithoutIcon(
  platform: DestinationPlatform,
  videoType: PlatformVideoType,
) {
  return omit(DESTINATION_PRESETS?.[platform]?.[videoType], 'icon');
}

export const YOUTUBE_OPTIONS: SocialPresetOption[] = [
  {
    ...getPresetWithoutIcon('youtube', 'short'),
    text: 'SHORT',
  },
  {
    ...getPresetWithoutIcon('youtube', 'clip'),
    text: 'CLIP',
  },
];

export const INSTAGRAM_OPTIONS: SocialPresetOption[] = [
  {
    ...getPresetWithoutIcon('instagram', 'reel'),
    text: 'REEL',
  },
  {
    ...getPresetWithoutIcon('instagram', 'story'),
    text: 'STORY',
  },
  {
    ...getPresetWithoutIcon('instagram', 'post'),
    text: 'POST',
  },
];

export const FACEBOOK_OPTIONS: SocialPresetOption[] = [
  {
    ...getPresetWithoutIcon('facebook', 'reel'),
    text: 'REEL',
  },
  {
    ...getPresetWithoutIcon('facebook', 'story'),
    text: 'STORY',
  },
  {
    ...getPresetWithoutIcon('facebook', 'post'),
    text: 'POST',
  },
];

export const TIKTOK_OPTIONS: SocialPresetOption[] = [
  {
    ...getPresetWithoutIcon('tiktok', 'story'),
    text: 'STORY',
  },
  {
    ...getPresetWithoutIcon('tiktok', 'post'),
    text: 'POST',
  },
];

export const MORE_OPTIONS: SocialPresetOption[] = [
  {
    ...DESTINATION_PRESETS.twitter.post,
    icon: <X width="12px" height="10px" className={block('x-icon')} />,
    text: 'X (TWITTER)',
  },
  {
    ...DESTINATION_PRESETS.linkedin.post,
    icon: 'linkedin',
    text: 'LINKEDIN',
  },
  {
    ...DESTINATION_PRESETS.more.snapchat,
    key: 'SnapChat',
    icon: 'snapchat-ghost',
    text: 'SNAPCHAT',
  },
  {
    ...DESTINATION_PRESETS.bluesky.videoFeeds,
    icon: <Bluesky />,
    text: 'BLUESKY',
  },
  {
    ...DESTINATION_PRESETS.more.pinterest,
    key: 'Pinterest',
    icon: <Pinterest />,
    text: 'PINTEREST',
  },
  {
    ...DESTINATION_PRESETS.threads.post,
    icon: <Threads />,
    text: 'THREADS',
  },
  {
    ...FULLCLIP_PRESET,
    hideDuration: true,
    icon: 'arrows-alt-h',
    text: 'SELECT FULL CLIP',
    useMaxAvailableDuration: true,
  },
];

export const SOCIAL_PRESET_POPOVER_OPTIONS: Partial<Record<
  DestinationPreset['key'],
  SocialPresetPopoverConfig
>> = {
  YouTubeEpisode: {
    ...DESTINATION_PRESETS.youtube.episode,
    icon: YoutubeRounded,
  },
  YouTubeClip: {
    ...DESTINATION_PRESETS.youtube.clip,
    icon: YoutubeRounded,
  },
  YouTubeShort: {
    ...DESTINATION_PRESETS.youtube.short,
    icon: YoutubeRounded,
  },
  InstagramReel: {
    ...DESTINATION_PRESETS.instagram.reel,
    icon: InstagramRounded,
  },
  InstagramStory: {
    ...DESTINATION_PRESETS.instagram.story,
    icon: InstagramRounded,
  },
  InstagramPost: {
    ...DESTINATION_PRESETS.instagram.post,
    icon: InstagramRounded,
  },
  FacebookReel: {
    ...DESTINATION_PRESETS.facebook.reel,
    icon: FacebookRounded,
  },
  FacebookStory: {
    ...DESTINATION_PRESETS.facebook.story,
    icon: FacebookRounded,
  },
  FacebookPost: {
    ...DESTINATION_PRESETS.facebook.post,
    icon: FacebookRounded,
  },
  FacebookEpisode: {
    ...DESTINATION_PRESETS.facebook.episode,
    icon: FacebookRounded,
  },
  TikTokPost: {
    ...DESTINATION_PRESETS.tiktok.post,
    icon: TiktokRounded,
  },
  TikTokStory: {
    ...DESTINATION_PRESETS.tiktok.story,
    icon: TiktokRounded,
  },
  LinkedInPost: {
    ...DESTINATION_PRESETS.linkedin.post,
    icon: React.Fragment,
  },
  ThreadsPost: {
    ...DESTINATION_PRESETS.threads.post,
    icon: React.Fragment,
  },
  TwitterPost: {
    ...DESTINATION_PRESETS.twitter.post,
    icon: React.Fragment,
  },
  BlueskyVideoFeeds: {
    ...DESTINATION_PRESETS.bluesky.videoFeeds,
    icon: React.Fragment,
  },
  Custom: {
    ...FULLCLIP_PRESET,
    icon: React.Fragment,
  },
  FullClip: {
    ...FULLCLIP_PRESET,
    icon: React.Fragment,
  },
  Pinterest: {
    ...DESTINATION_PRESETS.more.pinterest,
    icon: Pinterest,
  },
  SnapChat: {
    ...DESTINATION_PRESETS.more.snapchat,
    icon: React.Fragment,
  },
};

export const isPresetBlocked = (
  selectedPreset?: DestinationPreset | DestinationPreset['key'],
): boolean =>
  !!SOCIAL_PRESET_BLOCKING_OPTIONS.includes(
    typeof selectedPreset === 'string' ? selectedPreset : selectedPreset?.key,
  );
